import React, { useRef, useState } from 'react';
import { Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useChipFit } from '@/hooks/useChipFit';

// One line of small chips plus the cell's vertical padding
const COLLAPSED_HEIGHT = 40;

/**
 * Chips in a table cell that collapse to one line. Collapsed, it shows the chips that fit and
 * "+x" for the rest, so the row's height comes from the other cells. The arrow expands it to
 * every chip, wrapped, and collapses it again, as the feature flag targeting cells do.
 * renderChip(item) renders each item's chip (with its key); itemsLabel names them for the
 * arrow (e.g. 'broker groups').
 */
export const ExpandableChips = ({ items, renderChip, itemsLabel }) => {
	const chipsRef = useRef();
	const [expanded, setExpanded] = useState(false);
	// Every chip is shown from the start of expanding until collapsing has finished, so the
	// chips stay in place while the height animates
	const [showAll, setShowAll] = useState(false);

	const fitCount = useChipFit(chipsRef, items.length);
	const hiddenCount = showAll ? 0 : Math.max(items.length - fitCount, 0);
	const shownItems = showAll ? items : items.slice(0, fitCount);

	const handleToggle = (event) => {
		// Expanding or collapsing shouldn't also click the row
		event.stopPropagation();
		if (!expanded) {
			setShowAll(true);
		}
		setExpanded(!expanded);
	};

	return (
		<Stack direction='row' alignItems='center' spacing={0.5} sx={{ width: '100%', height: '100%' }}>
			<Collapse
				in={expanded}
				collapsedSize={COLLAPSED_HEIGHT}
				onExited={() => setShowAll(false)}
				sx={{ flex: 1, minWidth: 0 }}
			>
				<Stack
					ref={chipsRef}
					direction='row'
					flexWrap={showAll ? 'wrap' : 'nowrap'}
					gap={0.5}
					alignItems='center'
					sx={{ minWidth: 0, minHeight: COLLAPSED_HEIGHT, overflow: 'hidden', py: 1 }}
				>
					{shownItems.map(renderChip)}
					{hiddenCount > 0 && (
						<Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
							{`+${hiddenCount}`}
						</Typography>
					)}
				</Stack>
			</Collapse>
			{(showAll || hiddenCount > 0) && (
				<Tooltip title={expanded ? 'Show less' : `Show all ${itemsLabel}`}>
					<IconButton
						size='small'
						onClick={handleToggle}
						aria-label={expanded ? `Show fewer ${itemsLabel}` : `Show all ${itemsLabel}`}
						aria-expanded={expanded}
					>
						{expanded ? (
							<KeyboardArrowUpIcon fontSize='small' />
						) : (
							<KeyboardArrowDownIcon fontSize='small' />
						)}
					</IconButton>
				</Tooltip>
			)}
		</Stack>
	);
};

ExpandableChips.displayName = 'ExpandableChips';
